import { useState, type FormEvent } from 'react'
import { ApiError } from '@/api/errors'
import type { QCSeverity } from '@/api/types'
import { qcSeverityLabel } from '@/i18n/labels'
import { useDepartments } from '@/features/refs/queries'
import { Button, Field, Input, Select, Sheet, Textarea } from '@/ui'
import { useCreateQCRemark } from './queries'

const SEVERITIES: QCSeverity[] = ['MINOR', 'MAJOR', 'CRITICAL']

export function QCCreateSheet({
  orderId,
  open,
  onClose,
}: {
  orderId: string
  open: boolean
  onClose: () => void
}) {
  const create = useCreateQCRemark(orderId)
  const departments = useDepartments()
  const [severity, setSeverity] = useState<QCSeverity>('MAJOR')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [targetDept, setTargetDept] = useState('')

  const errMessage = create.error instanceof ApiError ? create.error.message : null

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!targetDept) return
    create.mutate(
      {
        severity,
        title: title.trim(),
        description: description.trim() || undefined,
        target_department_id: targetDept,
      },
      {
        onSuccess: () => {
          setTitle('')
          setDescription('')
          onClose()
        },
      },
    )
  }

  return (
    <Sheet open={open} onClose={onClose} title="Новое замечание ОТК">
      <form onSubmit={submit}>
        <Field label="Серьёзность">
          <Select value={severity} onChange={(e) => setSeverity(e.target.value as QCSeverity)}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {qcSeverityLabel[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Заголовок *">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label="Описание">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Отдел-адресат *">
          <Select value={targetDept} onChange={(e) => setTargetDept(e.target.value)} required>
            <option value="">— выберите отдел —</option>
            {(departments.data?.items ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        {errMessage && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red">{errMessage}</p>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" fullWidth loading={create.isPending} disabled={!targetDept}>
            Создать
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
